import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  orderChannels,
  readChannelOrder,
  writeChannelOrder,
  type OrderedChannel,
} from './channelOrder'

export function useChannelOrder<T extends OrderedChannel>(
  buildingId: number,
  channels: readonly T[],
) {
  const [savedBuildingId, setSavedBuildingId] = useState(buildingId)
  const [savedIds, setSavedIds] = useState<readonly string[]>(() => readChannelOrder(buildingId))

  if (savedBuildingId !== buildingId) {
    setSavedBuildingId(buildingId)
    setSavedIds(readChannelOrder(buildingId))
  }

  const ordered = useMemo(
    () => orderChannels(channels, savedIds),
    [channels, savedIds],
  )

  useEffect(() => {
    if (channels.length === 0) return
    const liveIds = new Set(channels.map((channel) => String(channel.id)))
    const pruned = savedIds.filter((id) => liveIds.has(id))
    if (pruned.length === savedIds.length) return
    writeChannelOrder(buildingId, pruned)
    setSavedIds(pruned)
  }, [buildingId, channels, savedIds])

  const commitOrder = useCallback((ids: readonly string[]) => {
    writeChannelOrder(buildingId, ids)
    setSavedIds(ids)
  }, [buildingId])

  return { ordered, commitOrder }
}
